'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useDocumentViewer, VIEWER_RESIZE_STEP } from './viewer-context';

/**
 * The drag handle on the viewer's inner (left) edge — the border it shares
 * with the page. Same control in both dock styles: embedded, it moves the
 * split and the page reflows beside it; floating, it widens or narrows the
 * panel over the page.
 *
 * The value is the PANEL's width, so moving the edge left (pointer or
 * ArrowLeft) makes it larger. Double-click resets to the default for the
 * current dock style.
 */
export function ViewerResizeHandle() {
  const { panelWidth, widthBounds, setPanelWidth, resetPanelWidth, setResizing } =
    useDocumentViewer();

  // Where the gesture started; null when not dragging.
  const drag = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);
  // The latest width during a drag, for the persist on release.
  const lastWidth = useRef(panelWidth);
  lastWidth.current = panelWidth;

  // Text selection and the cursor are page-wide while dragging: the pointer
  // leaves the 8px handle immediately, and without this the drag selects
  // half the document and flickers back to an arrow over the page.
  const restoreBody = useRef<(() => void) | null>(null);
  const lockBody = useCallback(() => {
    const body = document.body;
    const prev = { userSelect: body.style.userSelect, cursor: body.style.cursor };
    body.style.userSelect = 'none';
    body.style.cursor = 'col-resize';
    restoreBody.current = () => {
      body.style.userSelect = prev.userSelect;
      body.style.cursor = prev.cursor;
    };
  }, []);
  const endDrag = useCallback(() => {
    if (!drag.current) return;
    drag.current = null;
    restoreBody.current?.();
    restoreBody.current = null;
    setResizing(false);
  }, [setResizing]);
  // Never leave the page unselectable if the panel closes mid-drag.
  useEffect(() => () => {
    restoreBody.current?.();
    restoreBody.current = null;
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    drag.current = { pointerId: e.pointerId, startX: e.clientX, startWidth: panelWidth };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // Capture is an enhancement (keeps the drag when the pointer outruns
      // the handle); the drag still works without it.
    }
    lockBody();
    setResizing(true);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    // Left edge: moving the pointer left grows the panel.
    setPanelWidth(d.startWidth + (d.startX - e.clientX));
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Already released (e.g. lostpointercapture) — nothing to undo.
    }
    setPanelWidth(d.startWidth + (d.startX - e.clientX), { persist: true });
    endDrag();
  };
  const onPointerCancel = () => {
    if (!drag.current) return;
    setPanelWidth(lastWidth.current, { persist: true });
    endDrag();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let next: number | null = null;
    if (e.key === 'ArrowLeft') next = panelWidth + VIEWER_RESIZE_STEP;
    else if (e.key === 'ArrowRight') next = panelWidth - VIEWER_RESIZE_STEP;
    else if (e.key === 'Home') next = widthBounds.min;
    else if (e.key === 'End') next = widthBounds.max;
    else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      resetPanelWidth();
      return;
    }
    if (next === null) return;
    e.preventDefault();
    // The panel's own key handler (Escape, Ctrl+F, zoom) has nothing to do
    // with these keys; keep them here.
    e.stopPropagation();
    setPanelWidth(next, { persist: true });
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize document viewer"
      aria-valuenow={panelWidth}
      aria-valuemin={widthBounds.min}
      aria-valuemax={widthBounds.max}
      aria-valuetext={`${panelWidth} pixels wide`}
      title="Drag to resize · double-click to reset"
      tabIndex={0}
      data-testid="viewer-resize-handle"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onLostPointerCapture={endDrag}
      onDoubleClick={() => resetPanelWidth()}
      onKeyDown={onKeyDown}
      // Straddles the border: 8px wide, centred on the panel's left edge, so
      // it is as easy to grab from the page side as from the panel side.
      className="group absolute inset-y-0 left-0 z-10 w-2 -translate-x-1/2 cursor-col-resize touch-none select-none outline-none"
    >
      {/* The visible affordance: a hairline that thickens and takes the
          accent on hover, drag and keyboard focus. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-transparent transition-[background-color,width] duration-150 group-hover:w-0.5 group-hover:bg-accent group-focus-visible:w-0.5 group-focus-visible:bg-accent group-active:w-0.5 group-active:bg-accent motion-reduce:transition-none"
      />
    </div>
  );
}
